import React, { useState, useCallback, useMemo } from 'react';
import { TextField } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MORE_AVAILABLE_OPTION_ID } from '~/modules/common/components/SearchAutocomplete/SearchAutocomplete';
import CheckboxOption from '../CheckboxOption';
import { useProjectDropdownOptions } from './useProjectDropdownOptions';

const ARCHIVED_PROJECT_STATUS_URI = 'urn:replicon:project-status-type:archived';

export const useProjectDropdownHandlers = ({
  value,
  showInactiveProjects,
  onToggleInactiveProjects,
  onClose,
  projectManagerUri,
  label,
  onChange,
  classes,
  onInputChange
}) => {
  const { formatMessage } = useIntl();
  const [searchTerm, setSearchTerm] = useState('');

  const handleInputChange = useCallback(
    (event, newValue, reason) => {
      setSearchTerm(newValue);
      if (onInputChange) {
        onInputChange(event, newValue, reason);
      }
    },
    [onInputChange]
  );

  const showInactiveProjectsOption = useMemo(
    () => ({
      id: 'show-inactive-projects',
      displayText: formatMessage({
        id: 'moveTimeDialog.showArchivedProjects'
      }),
      isCheckbox: true
    }),
    [formatMessage]
  );

  const handleChange = useCallback(
    (selectedValue, event, reason) => {
      if (selectedValue && selectedValue.id === showInactiveProjectsOption.id) {
        onToggleInactiveProjects();

        return;
      }

      if (onChange) {
        onChange(selectedValue, event, reason);
      }

      if (onClose) {
        onClose();
      }
    },
    [onChange, onClose, onToggleInactiveProjects, showInactiveProjectsOption.id]
  );

  const { loading, projects, hasMore } = useProjectDropdownOptions({
    filter: {
      text: value && value.id ? '' : searchTerm,
      projectManagers: projectManagerUri ? [projectManagerUri] : null,
      excludeProjectStatus: showInactiveProjects
        ? undefined
        : [ARCHIVED_PROJECT_STATUS_URI]
    }
  });

  const options = useMemo(() => {
    const baseOptions = [showInactiveProjectsOption];

    if (loading)
      return [
        ...baseOptions,
        {
          id: 'loading-projects',
          displayText: formatMessage({
            id: 'moveTimeDialog.loading'
          }),
          isLoading: true,
          disabled: true
        }
      ];

    if (projects.length === 0)
      return [
        ...baseOptions,
        {
          id: 'no-options',
          displayText: formatMessage({
            id: 'moveTimeDialog.noMatchingProjects'
          }),
          isNoOptions: true,
          disabled: true
        }
      ];

    return [...baseOptions, ...projects];
  }, [showInactiveProjectsOption, loading, projects, formatMessage]);

  const filterOptions = useCallback(
    (allOptions, state) => {
      const checkboxOption = allOptions.find(
        opt => opt.id === showInactiveProjectsOption.id
      );
      const otherOptions = allOptions.filter(
        opt => opt.id !== showInactiveProjectsOption.id
      );

      const filtered = otherOptions.filter(option => {
        if (option.isLoading || !state.inputValue) return true;

        return option.displayText
          ?.toLowerCase()
          .includes(state.inputValue.toLowerCase());
      });

      if (state.inputValue && filtered.length === 0) {
        const noOptionsItem = {
          id: 'no-options',
          displayText: formatMessage({
            id: 'moveTimeDialog.noMatchingProjects'
          }),
          isNoOptions: true,
          disabled: true
        };

        return checkboxOption
          ? [checkboxOption, noOptionsItem]
          : [noOptionsItem];
      }

      return checkboxOption ? [checkboxOption, ...filtered] : filtered;
    },
    [formatMessage, showInactiveProjectsOption.id]
  );

  const getOptionLabel = useCallback(
    option => {
      if (option.id === showInactiveProjectsOption.id) return '';

      return option.displayText;
    },
    [showInactiveProjectsOption.id]
  );

  const getOptionSelected = useCallback(
    (option, selected) => {
      if (option.id === showInactiveProjectsOption.id) return false;

      return option.id === selected.id;
    },
    [showInactiveProjectsOption.id]
  );

  const getOptionDisabled = useCallback(
    option =>
      option.id === MORE_AVAILABLE_OPTION_ID ||
      option.isLoading ||
      option.isNoOptions,
    []
  );

  const renderInput = useCallback(
    params => <TextField {...params} label={label} />,
    [label]
  );

  const handleCheckboxKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onToggleInactiveProjects();
      }
    },
    [onToggleInactiveProjects]
  );

  const renderOption = useCallback(
    option => {
      if (option.id === showInactiveProjectsOption.id) {
        return (
          <CheckboxOption
            checked={showInactiveProjects}
            label={formatMessage({
              id: 'moveTimeDialog.showArchivedProjects'
            })}
            onChange={onToggleInactiveProjects}
            onKeyDown={handleCheckboxKeyDown}
            option={option}
          />
        );
      }

      const optionClassName = option.isClosed ? classes.closedProject : '';

      return <div className={optionClassName}>{option.displayText}</div>;
    },
    [
      showInactiveProjectsOption.id,
      classes.closedProject,
      showInactiveProjects,
      formatMessage,
      onToggleInactiveProjects,
      handleCheckboxKeyDown
    ]
  );

  return {
    loading,
    hasMore,
    options,
    handleInputChange,
    filterOptions,
    getOptionLabel,
    getOptionSelected,
    getOptionDisabled,
    renderInput,
    renderOption,
    handleChange,
    showInactiveProjectsOptionId: showInactiveProjectsOption.id
  };
};

export default useProjectDropdownHandlers;
